import { useEffect, useMemo, useRef, useState } from "react";
import { bindActionCreators } from "redux";
import { connect, type ConnectedProps } from "react-redux";
import { noop, uniq } from "lodash";
import { Icon } from "semantic-ui-react";
import { SwitchEntityTypes } from "enums/switchEntityTypes";
import { useObserver } from "../../../../../hooks/useObserver";
import { type AppDispatch, type RootState } from "../../../../Application/globaltypes/redux";
import { publishDraftFlowEntity } from "../../state/actions/flowEntityStateActionCreators";
import { resetLicensingInfo } from "../../state/slices/flowLicensingSlice";
import { fetchLicensingInfo } from "../../state/thunks/flowLicensingThunk";
import { resetContentPacksAction } from "../../../../Licensing/ContentAssignmentModalLicensingSteps/state/thunks/assignmentModalLicensingThunk";
import { fetchFlowTags as fetchFlowTagsThunk } from "../../state/thunks/flowBaseThunk";
import { fetchFailure as licensesFetchFailure } from "../../../../Licensing/ContentAssignmentModalLicensingSteps/state/slices/licenseConfirmationSlice";
import ContentAssignmentModalTwoSteps from "../../../../../components/assignmentModals/contentAssignmentModal/contentAssignmentModalTwoSteps/ContentAssignmentModalTwoSteps";
import { NotifyStepConfig, type NotifyStepConfirmPayload } from "../../../../SystemNotifications/types";
import { TemplateTypes } from "../../../../../enums";
import { flowDesignerSelector, flowPackContextItemsSelector, selectFlowHasBeenPublished } from "../../state/selectors";
import ObjectUtils from "../../../../../utils/objectUtils";
import { type AddedAsset } from "../../types/flowLicensing";
import { type PacksContextItem, bindAction, type IObservable } from "interfaces";
import { shouldShowModalForFlowPublish } from "features/Licensing/ContentAssignmentModalLicensingSteps/state/thunks/licensingModalThunk";
import { reset } from "features/Licensing/ContentAssignmentModalLicensingSteps/state/slices/licensingModalSlice";
import styles from "./contentAssignmentsContainer.module.scss";
import { useEventCallback } from "hooks/useEventCallback";
import useToggle from "../../../../../hooks/useToggle";
import PaymentModal from "../../../../Licensing/Packs/PaymentModal/PaymentModal";
import NotificationModal, {
  type NotificationModalState,
} from "../../../../../components/modal/NotificationModal/NotificationModal";
import Observable from "../../../../../utils/Observable";
import { useFeatureFlag } from "../../../../../hooks/useFeatureFlag";
import { FeatureFlags } from "../../../../../featureFlags";
import { type LicenseConfirmationFormRef } from "../../../../Licensing/ContentAssignmentModalLicensingSteps/LicenseConfirmationModalContent/LicenseConfirmationBlockV2/LicenseConfirmationBlock";
import { updateLicensePackInfo } from "../../../../Licensing/ContentAssignmentModalLicensingSteps/state/slices/licenseConfirmationInfoSlice";

interface ContentAssignmentsContainerProps {
  flowId: number;
  flowHasOrigin: boolean;
  publishClickObserver: IObservable<(then: () => void) => void>;
}

export type ContentAssignmentsContainerPropsAll = PropsFromRedux & ContentAssignmentsContainerProps;

export const ContentAssignmentsContainer = (props: ContentAssignmentsContainerPropsAll) => {
  const {
    flowId,
    flowHasOrigin,
    onLicenseConfirmed,
    resetContentPacks,
    packContextItems,
    flowAssignmentsItems,
    fetchFlowTags,
    flowChanges: { isDefinitionChanged },
    flowPackContextItems,
    fetchLicensing,
    resetLicensing,
    shouldShowLicensingModal,
    resetLicensingModal,
    showNotify,
    hasBeenPublished,
    updateLicensePackInfo,
    paymentPacks,
    isPurchaseNeeded,
  } = props;

  const [showModal, setShowModal] = useState(false);
  const [showPaymentModal, togglePaymentModal] = useToggle(false);
  const [skipLicensingSteps, setSkipLicensingSteps] = useState(false);
  const onConfirmRef = useRef(noop);
  const [subsribeOnPublishClick] = useObserver(props.publishClickObserver);
  const packInfoRef = useRef(flowPackContextItems);
  const onTriggerNotificationModalObserver = useMemo(() => new Observable(), []);

  const additionalPurchaseEnabled = useFeatureFlag(FeatureFlags.AdditionalPurchase);
  const licenseConfirmationFormRef = useRef<LicenseConfirmationFormRef>({
    getValues: () => [],
  });

  /* istanbul ignore next */
  useEffect(() => {
    const callback = (then: () => void) => {
      onConfirmRef.current = then;
      onPublishButtonClick();
    };

    return subsribeOnPublishClick(callback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subsribeOnPublishClick]);

  useEffect(() => {
    packInfoRef.current = flowPackContextItems;
  }, [flowPackContextItems]);

  const onPublishButtonClick = () => {
    fetchLicensing(flowId, triggerPublishFlow);
  };

  const onCloseModal = useEventCallback(() => {
    setShowModal(false);
    setSkipLicensingSteps(false);
    resetLicensing();
    resetContentPacks();
    resetLicensingModal();
  }, [resetContentPacks, resetLicensing, resetLicensingModal]);

  const onConfirm = useEventCallback(
    (notificationConfirmPayload?: NotifyStepConfirmPayload) => {
      const packIds = uniq([
        ...packContextItems.map((p) => p.packId!),
        ...flowAssignmentsItems.flatMap((item) => item.packIds),
      ]);
      fetchFlowTags();
      onLicenseConfirmed(flowId, packIds, notificationConfirmPayload);
      onConfirmRef.current();
      onCloseModal();
    },
    [fetchFlowTags, flowAssignmentsItems, flowId, onCloseModal, onLicenseConfirmed, packContextItems],
  );

  const triggerPublishFlow = useEventCallback(
    async (flowChanged: boolean, addedAssets: AddedAsset[]) => {
      packInfoRef.current = packInfoRef.current.filter((i: PacksContextItem) =>
        addedAssets.some((a) => a.id === i.id && a.type.toLowerCase() === i.type.toLowerCase()),
      );

      if (flowChanged && hasBeenPublished) {
        const showLicensing = await shouldShowLicensingModal(flowId, packInfoRef.current);
        if (!showNotify && !showLicensing) {
          onConfirm();
          return;
        }
        setSkipLicensingSteps(!showLicensing);
        setShowModal(true);
      } else {
        onConfirm();
      }
    },
    [shouldShowLicensingModal, flowId, showNotify, onConfirm, hasBeenPublished],
  );

  const renderResetFlowProgressAlert = () => (
    <div className={styles["reset-flow-progress-alert"]}>
      <Icon className="info circle" />
      <div className={styles["reset-flow-progress-alert-content"]}>
        <b>Flow progress will be reset. </b>
        <span>
          Publishing these changes will reset Flow progress to the <br /> beginning for all users in progress with this
          Flow.
        </span>
      </div>
    </div>
  );

  const handleConfirm = (notificationConfirmPayload?: NotifyStepConfirmPayload) => {
    if (additionalPurchaseEnabled && !skipLicensingSteps && isPurchaseNeeded) {
      updateLicensePackInfo(licenseConfirmationFormRef.current.getValues());
      setShowModal(false);
      togglePaymentModal();
    } else {
      onConfirm(notificationConfirmPayload);
    }
  };

  const onPaymentSuccess = () => {
    togglePaymentModal();
    const entityState: NotificationModalState = {
      entityType: SwitchEntityTypes.Flow,
      notifyConfigType: flowHasOrigin ? NotifyStepConfig.WithProgress : NotifyStepConfig.Default,
      templateType: flowHasOrigin ? TemplateTypes.FlowRepublished : TemplateTypes.AddedToFlowV2,
    };
    onTriggerNotificationModalObserver.notify(noop, entityState);
  };

  return (
    <>
      <ContentAssignmentModalTwoSteps
        showModal={showModal}
        flowId={flowId}
        flowHasOrigin={flowHasOrigin}
        onCancel={onCloseModal}
        onConfirm={handleConfirm}
        skipNotifyStep={!showNotify || (isPurchaseNeeded && additionalPurchaseEnabled && !skipLicensingSteps)}
        notifyStepDisabled={false}
        skipLicensing={skipLicensingSteps}
        packContextItems={packInfoRef.current}
        selectedUserIds={[]}
        selectedGroupIds={[]}
        notifyTemplateType={flowHasOrigin ? TemplateTypes.FlowRepublished : TemplateTypes.AddedToFlowV2}
        notifyConfigType={flowHasOrigin ? NotifyStepConfig.WithProgress : NotifyStepConfig.Default}
        entityType={SwitchEntityTypes.Flow}
        alert={flowHasOrigin && isDefinitionChanged && renderResetFlowProgressAlert()}
        skipPacksContext={skipLicensingSteps}
        licenseConfirmationFormRef={licenseConfirmationFormRef}
      />

      {additionalPurchaseEnabled && (
        <NotificationModal
          onTriggerPublishContentObserver={onTriggerNotificationModalObserver}
          onConfirmAction={onConfirm}
          onClose={onCloseModal}
        />
      )}

      {showPaymentModal && paymentPacks && (
        <PaymentModal
          paymentPacks={paymentPacks}
          onClose={() => {
            onCloseModal();
            togglePaymentModal();
          }}
          onPaymentSuccess={onPaymentSuccess}
        />
      )}
    </>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  isPurchaseNeeded: state.licensing?.availableLicenses?.isPurchaseNeeded,
  paymentPacks: state.licensing.licenseConfirmationInfo.paymentPacks,
  packContextItems: state.licensing.contentPacks.items,
  flowAssignmentsItems: state.licensing.contentPacks.flowAssignments,
  flowChanges: state.library.flows.base.licensing.flowChanges,
  flowPackContextItems: flowPackContextItemsSelector(state),
  hasExternalTrigger: ObjectUtils.isNotEmpty(flowDesignerSelector(state).externalTriggers.externalTriggerGroups),
  showNotify: state.library.flows.base.information.info.packs.length > 0,
  hasBeenPublished: selectFlowHasBeenPublished(state),
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onLicenseConfirmed: bindActionCreators(publishDraftFlowEntity, dispatch),
  fetchLicensing: bindAction(fetchLicensingInfo, dispatch),
  updateLicensePackInfo: bindAction(updateLicensePackInfo, dispatch),
  resetLicensing: bindAction(resetLicensingInfo, dispatch),
  setLicensesFetchFailure: (error: Error) => dispatch(licensesFetchFailure(error)),
  resetContentPacks: () => dispatch(resetContentPacksAction()),
  fetchFlowTags: () => dispatch(fetchFlowTagsThunk()),
  shouldShowLicensingModal: bindAction(shouldShowModalForFlowPublish, dispatch),
  resetLicensingModal: bindAction(reset, dispatch),
});

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(ContentAssignmentsContainer);
export default ConnectedComponent;
